/**
 * File Upload Component
 * Supports images and videos with preview
 */
import React, { useState, useRef, useCallback } from 'react';
import { Button } from './button';
import { Progress } from './progress';
import { 
  Upload, X, Image as ImageIcon, Film, FileText, 
  Loader2, CheckCircle, AlertCircle 
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';
import { uploadMedia, acceptFor } from '@/lib/mediaUpload';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Legacy `fileType` values map onto the media pipeline's purposes so every
 * existing caller keeps working while its files now land in object storage
 * (and phone-sized photos get downscaled on the way in).
 */
const PURPOSE_FOR = {
  logo: 'logo', favicon: 'favicon', hero: 'hero', background: 'banner',
  event: 'cover', gallery: 'gallery', image: 'photo', photo: 'photo',
  avatar: 'avatar', product: 'product', video: 'video',
  document: 'document', general: 'general',
};
const purposeFor = (fileType) => PURPOSE_FOR[fileType] || 'general';

export const FileUpload = ({
  value,
  onChange,
  accept,
  className,
  placeholder = 'Upload from your phone, tablet or computer',
  previewHeight = '200px',
  fileType = 'image',
}) => {
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [dragOver, setDragOver] = useState(false);
  const [error, setError] = useState(null);
  const fileInputRef = useRef(null);

  const getFileIcon = (type) => {
    if (type?.startsWith('image/')) return <ImageIcon className="h-12 w-12 text-slate-400" />;
    if (type?.startsWith('video/')) return <Film className="h-12 w-12 text-slate-400" />;
    return <FileText className="h-12 w-12 text-slate-400" />;
  };

  const uploadFile = async (file) => {
    setUploading(true);
    setProgress(0);
    setError(null);
    try {
      const asset = await uploadMedia(file, {
        purpose: purposeFor(fileType),
        onProgress: setProgress,
      });
      onChange?.(asset.url, asset);
      toast.success(`${asset.filename} uploaded`);
    } catch (err) {
      const msg = err?.message || 'That file could not be uploaded';
      setError(msg);
      toast.error(msg);
    } finally {
      setUploading(false);
    }
  };

  const handleFileSelect = useCallback((e) => {
    const file = e.target.files?.[0];
    if (file) uploadFile(file);
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files?.[0];
    if (file) uploadFile(file);
  }, []);

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    setDragOver(true);
  }, []);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    setDragOver(false);
  }, []);

  const clearFile = () => {
    onChange?.('');
    setError(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const isImage = value && (
    value.includes('.jpg') || 
    value.includes('.jpeg') || 
    value.includes('.png') || 
    value.includes('.gif') || 
    value.includes('.webp')
  );

  const isVideo = value && (
    value.includes('.mp4') || 
    value.includes('.webm') || 
    value.includes('.mov')
  );

  return (
    <div className={cn("relative", className)}>
      <input
        ref={fileInputRef}
        type="file"
        accept={accept || acceptFor(purposeFor(fileType))}
        onChange={handleFileSelect}
        className="hidden"
      />

      {value ? (
        <div className="relative border rounded-lg overflow-hidden bg-slate-50">
          {isImage && (
            <img 
              src={value} 
              alt="Uploaded" 
              className="w-full object-cover"
              style={{ height: previewHeight }}
            />
          )}
          {isVideo && (
            <video 
              src={value} 
              controls 
              className="w-full"
              style={{ height: previewHeight }}
            />
          )}
          {!isImage && !isVideo && (
            <div 
              className="flex items-center justify-center bg-slate-100"
              style={{ height: previewHeight }}
            >
              <a 
                href={value} 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-emerald-600 hover:underline flex items-center gap-2"
              >
                <FileText className="h-5 w-5" />
                View File
              </a>
            </div>
          )}
          <div className="absolute top-2 right-2 flex gap-2">
            <Button
              type="button"
              variant="secondary"
              size="sm"
              onClick={() => fileInputRef.current?.click()}
              className="h-8"
            >
              Replace
            </Button>
            <Button
              type="button"
              variant="destructive"
              size="sm"
              onClick={clearFile}
              className="h-8 w-8 p-0"
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        </div>
      ) : (
        <div
          onClick={() => fileInputRef.current?.click()}
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          className={cn(
            "border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors",
            dragOver 
              ? "border-emerald-500 bg-emerald-50" 
              : "border-slate-300 hover:border-emerald-400 hover:bg-slate-50",
            error && "border-red-300 bg-red-50"
          )}
          style={{ minHeight: previewHeight }}
        >
          {uploading ? (
            <div className="flex flex-col items-center gap-4">
              <Loader2 className="h-12 w-12 text-emerald-600 animate-spin" />
              <div className="w-full max-w-xs">
                <Progress value={progress} className="h-2" />
                <p className="text-sm text-slate-500 mt-2">{progress}% uploaded</p>
              </div>
            </div>
          ) : (
            <div className="flex flex-col items-center gap-3">
              {error ? (
                <AlertCircle className="h-12 w-12 text-red-400" />
              ) : (
                <Upload className="h-12 w-12 text-slate-400" />
              )}
              <div>
                <p className={cn(
                  "font-medium",
                  error ? "text-red-600" : "text-slate-700"
                )}>
                  {error || placeholder}
                </p>
                <p className="text-sm text-slate-500 mt-1">
                  Images up to 20MB · video up to 1GB · documents up to 100MB
                </p>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// Multi-file upload for galleries
export const GalleryUpload = ({
  value = [],
  onChange,
  maxFiles = 20,
  className,
}) => {
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  const uploadFiles = async (files) => {
    if (value.length + files.length > maxFiles) {
      toast.error(`Maximum ${maxFiles} files allowed`);
      return;
    }
    setUploading(true);
    const newUrls = [];
    for (const file of files) {
      try {
        // eslint-disable-next-line no-await-in-loop
        const asset = await uploadMedia(file, { purpose: 'gallery' });
        newUrls.push({ url: asset.url, type: asset.kind === 'video' ? 'video' : 'image',
                       name: asset.filename });
      } catch (err) {
        toast.error(`${file.name}: ${err.message}`);
      }
    }

    if (newUrls.length > 0) {
      onChange?.([...value, ...newUrls]);
      toast.success(`${newUrls.length} file(s) uploaded`);
    }
    setUploading(false);
  };

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length > 0) uploadFiles(files);
  };

  const removeFile = (index) => {
    const newValue = value.filter((_, i) => i !== index);
    onChange?.(newValue);
  };

  return (
    <div className={cn("space-y-4", className)}>
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
        {value.map((item, index) => (
          <div key={index} className="relative aspect-square rounded-lg overflow-hidden bg-slate-100 group">
            {item.type === 'video' ? (
              <video src={item.url} className="w-full h-full object-cover" />
            ) : (
              <img src={item.url} alt={item.name || ''} className="w-full h-full object-cover" />
            )}
            <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
              <Button
                type="button"
                variant="destructive"
                size="sm"
                onClick={() => removeFile(index)}
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
            {item.type === 'video' && (
              <div className="absolute top-2 left-2">
                <Film className="h-5 w-5 text-white drop-shadow" />
              </div>
            )}
          </div>
        ))}

        {value.length < maxFiles && (
          <div
            onClick={() => fileInputRef.current?.click()}
            className="aspect-square border-2 border-dashed rounded-lg flex flex-col items-center justify-center cursor-pointer hover:border-emerald-500 hover:bg-emerald-50 transition-colors"
          >
            {uploading ? (
              <Loader2 className="h-8 w-8 text-emerald-600 animate-spin" />
            ) : (
              <>
                <Upload className="h-8 w-8 text-slate-400" />
                <span className="text-sm text-slate-500 mt-2">Add files</span>
              </>
            )}
          </div>
        )}
      </div>

      <input
        ref={fileInputRef}
        type="file"
        accept="image/*,video/*"
        multiple
        onChange={handleFileSelect}
        className="hidden"
      />

      <p className="text-xs text-slate-500">
        {value.length} of {maxFiles} files
      </p>
    </div>
  );
};

export default FileUpload;
